import { useApiClient } from '@m43/core';
import {
    ConnectionStatusEnumType,
    ConnectionStatusReportView,
    ConnectionStatusView,
    MetricsResource,
} from '@mark43/cad-api';
import React from 'react';
import { v4 } from 'uuid';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { getM43LogRocket } from '../monitoring/logrocket';
import { CadNextSocketListener, useSocketStatus } from '../socket/use-stream';
import { ConnectionStatusContext, ConnectionStatusContextValue } from './connection-status-context';
import { convertCombinedConnectionStatusReportToFrontendConnectionStatusDto } from './converters';

let socketLastOfflineEpoch: number | undefined = undefined;

export function ConnectionStatusProvider(props: { children: React.ReactNode }) {
    const { isFirstResponder, isCadLegacyElectron, isElectronRewrite } = useApplicationType();

    const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatusContextValue>({
        SOCKET: 'ONLINE',
        INTERNET: electronBridge?.args['--connection-status'] ?? 'ONLINE',
        MARK43_PING: electronBridge?.args['--mark43-ping-status'] ?? 'ONLINE',
        DNS_RESOLUTION: 'ONLINE',
        FARO: 'ONLINE',
    });

    const apiClient = useApiClient();

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            invokeOnInitialize: false,
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.timesConnected < 1) return;
                const newStatus: ConnectionStatusEnumType =
                    socketStatusChangeEvent.status === 'CONNECTED' ? 'ONLINE' : 'OFFLINE';
                if (newStatus === 'OFFLINE' && !socketLastOfflineEpoch) {
                    socketLastOfflineEpoch = Date.now();
                }
                setConnectionStatus((current) => {
                    if (newStatus === current.SOCKET) return current;
                    return {
                        ...current,
                        SOCKET: newStatus,
                    };
                });
                apiClient.request<MetricsResource.PostConnectionStatusViews>({
                    method: 'POST',
                    url: 'metrics/report_connection_status/',
                    data: {
                        connectionStatusViews: [
                            {
                                source: 'SOCKET',
                                status: newStatus,
                                podName: socketStatusChangeEvent.podName,
                            },
                        ],
                    },
                });
                if (socketStatusChangeEvent.status === 'CONNECTED' && socketLastOfflineEpoch) {
                    const epoch = socketLastOfflineEpoch;
                    socketLastOfflineEpoch = undefined;
                    const delta = Date.now() - epoch;
                    const seconds = Math.round(delta / 1000);
                    apiClient.request<MetricsResource.PostOfflineSecondsViews>({
                        method: 'POST',
                        url: 'metrics/report_offline_seconds/',
                        data: {
                            offlineSecondsViews: [
                                {
                                    source: 'SOCKET',
                                    offlineSeconds: seconds,
                                },
                            ],
                        },
                    });
                }
            },
        }),
        [apiClient]
    );

    useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (isCadLegacyElectron) {
            const onInternetConnectionStatusUpdate = (
                connectionStatus: ConnectionStatusEnumType
            ) => {
                setConnectionStatus((current) => {
                    if (connectionStatus === current.INTERNET) return current;
                    return {
                        ...current,
                        INTERNET: connectionStatus,
                    };
                });
            };
            if (electronBridge && 'registerIsOnlineCallback' in electronBridge) {
                electronBridge.registerIsOnlineCallback(onInternetConnectionStatusUpdate);
            }
            setConnectionStatus((current) => ({
                ...current,
                INTERNET: electronBridge?.args['--connection-status'] ?? 'ONLINE',
            }));
        }
    }, [setConnectionStatus, isCadLegacyElectron]);

    React.useEffect(() => {
        if (electronBridge && 'registerConnectionStatusViewHandler' in electronBridge) {
            const onConnectionStatusViewUpdate = (view: ConnectionStatusView) => {
                setConnectionStatus((current) => {
                    if (current[view.source] !== view.status) {
                        return {
                            ...current,
                            [view.source]: view.status,
                        };
                    }
                    return current;
                });
            };
            electronBridge.registerConnectionStatusViewHandler?.(onConnectionStatusViewUpdate);
        }
    }, [isElectronRewrite]);

    React.useEffect(() => {
        const logRocket = getM43LogRocket();
        if (logRocket) {
            logRocket.log('connection status', connectionStatus);
        }
        // this entire if block can be removed when new electron is installed at all customers
        if (
            isCadLegacyElectron &&
            electronBridge?.args['--workspace-window-type'] === 'EVENT_V2' &&
            !window.Cypress
        ) {
            const combinedConnectionStatusReport: ConnectionStatusReportView = {
                uuid: v4(),
                timestamp: Date.now().toString(),
                internetStatus: {
                    status: connectionStatus.INTERNET,
                    // hardcoded because offlineSeconds is unused from the endpoint we call here
                    offlineSeconds: 0,
                },
                socketStatus: {
                    status: connectionStatus.SOCKET,
                    // hardcoded because offlineSeconds is unused from the endpoint we call here
                    offlineSeconds: 0,
                },
            };
            apiClient
                .request<MetricsResource.PostCombinedConnectionStatusReports>({
                    method: 'POST',
                    url: 'metrics/combined_connection_status_reports/',
                    data: { connectionStatusReportViews: [combinedConnectionStatusReport] },
                })
                .catch(() => {
                    if (electronBridge && 'storeConnectionStatusDto' in electronBridge) {
                        electronBridge.storeConnectionStatusDto?.(
                            convertCombinedConnectionStatusReportToFrontendConnectionStatusDto(
                                combinedConnectionStatusReport
                            )
                        );
                    }
                });
        }
    }, [apiClient, connectionStatus, isFirstResponder, isCadLegacyElectron]);

    return (
        <ConnectionStatusContext.Provider value={connectionStatus}>
            {props.children}
        </ConnectionStatusContext.Provider>
    );
}
